import type { FC } from 'react';
import { useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';
import { TextInput } from 'react-native-paper';

import { POST_TITLE_MAX_CHARS } from '@/features/post-editor/schemas/postForm';
import type { PostFormValues } from '@/features/post-editor/types/postForm';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    input: {
      fontSize: 17,
      fontWeight: '700',
      backgroundColor: theme.colors.surfaceContainerLow,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.outlineVariant
    }
  });

/**
 * Optional post title, bound to the shared form. Sits directly below the
 * channel selection. The hard 80-char cut happens via `maxLength`; the schema
 * only acts as a validation backstop. Intentionally has NO autoFocus — the
 * focus (and keyboard) stays on the post text field.
 */
export const PostTitleField: FC = () => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { t } = useTranslation(LocalizationNameSpace.POST_CREATION, {
    keyPrefix: 'screens.post-editor'
  });
  const { control } = useFormContext<PostFormValues>();
  const { field } = useController({ control, name: 'title' });

  return (
    <TextInput
      style={styles.input}
      accessibilityLabel={t('accessibility.titleLabel')}
      accessibilityHint={t('accessibility.titleHint')}
      placeholder={t('titlePlaceholder')}
      underlineColor="transparent"
      activeUnderlineColor="transparent"
      textColor={theme.colors.onSurface}
      placeholderTextColor={theme.colors.onSurfaceVariant}
      cursorColor={theme.colors.onSurfaceVariant}
      selectionColor={theme.colors.onSurfaceVariant}
      value={field.value}
      onChangeText={field.onChange}
      onBlur={field.onBlur}
      maxLength={POST_TITLE_MAX_CHARS}
    />
  );
};
